import { IN_HOUSE_LAB_TASK } from '../data/in-house/in-house.constants';
import { LAB_ORDER_TASK } from '../data/labs/labs.constants';
import { ERX_TASK, FAX_TASK, MANUAL_TASK, RADIOLOGY_TASK } from '../data/tasks/types';
import { DME_TASK } from './dme.types';
import { ProviderNotificationMethod } from './practitioner.types';

/**
 * Per-notification-type registration for staff: each type carries a delivery method, location
 * filter, and "assigned to" filter. Persisted as one JSON string in a child extension of
 * `PROVIDER_NOTIFICATIONS_SETTINGS_EXTENSION_URL` (see `getProviderNotificationPreferencesV2`).
 */

export type NotificationAssignedTo = 'me' | 'anyone';

export interface NotificationRowPref {
  enabled: boolean;
  method: ProviderNotificationMethod;
  /** Explicit location ids to match. Ignored when `allLocations` is true. */
  locationIds: string[];
  /** When true, match tasks at any location regardless of `locationIds`. */
  allLocations: boolean;
  assignedTo: NotificationAssignedTo;
}

/** Stable ids for the task categories shown in the settings table. */
export const UI_TASK_CATEGORY_IDS = [
  'externalLab',
  'inHouseLab',
  'radiology',
  'erx',
  'inHouseMedications',
  'nursingOrders',
  'dme',
  'patientFollowUp',
  'procedures',
  'charting',
  'coding',
  'billing',
  'inboundFax',
  'other',
] as const;
export type UiTaskCategoryId = (typeof UI_TASK_CATEGORY_IDS)[number];

export const UI_TASK_CATEGORY_LABELS: Record<UiTaskCategoryId, string> = {
  externalLab: 'External Lab',
  inHouseLab: 'In-house Lab',
  radiology: 'Radiology',
  erx: 'eRX',
  inHouseMedications: 'In-house Medications',
  nursingOrders: 'Nursing Orders',
  dme: 'DME',
  patientFollowUp: 'Patient Follow-up',
  procedures: 'Procedures',
  charting: 'Charting',
  coding: 'Coding',
  billing: 'Billing',
  inboundFax: 'Inbound Fax',
  other: 'Other',
};

/**
 * Maps a `Task.groupIdentifier.value` category code to a UI category id. Several task-category codes fold
 * into one UI category (e.g. auto-generated `external-lab` and `manual-external-lab` → "External Lab").
 *
 * A category listed here is what gives a task category a settings row and makes the notifications-updater
 * cron's category engine notify its subscribers.
 *
 * Unmapping a category does NOT silence it: `resolveAssignmentDelivery` reads an unmapped category as
 * "no V2 row to consult" and hands the decision to the legacy always-on `taskNotificationsEnabled` flag,
 * so an *assigned* task in an unmapped category still notifies its owner — including staff who had that
 * category switched off. Only the creation-time notification goes away.
 */
export const TASK_CODE_TO_UI_CATEGORY: Record<string, UiTaskCategoryId> = {
  [LAB_ORDER_TASK.category]: 'externalLab',
  [MANUAL_TASK.category.externalLab]: 'externalLab',
  [IN_HOUSE_LAB_TASK.category]: 'inHouseLab',
  [MANUAL_TASK.category.inHouseLab]: 'inHouseLab',
  [RADIOLOGY_TASK.category]: 'radiology',
  [MANUAL_TASK.category.radiology]: 'radiology',
  [ERX_TASK.category]: 'erx',
  [MANUAL_TASK.category.erx]: 'erx',
  [MANUAL_TASK.category.inHouseMedications]: 'inHouseMedications',
  [MANUAL_TASK.category.nursingOrders]: 'nursingOrders',
  [DME_TASK.category]: 'dme',
  [MANUAL_TASK.category.patientFollowUp]: 'patientFollowUp',
  [MANUAL_TASK.category.procedures]: 'procedures',
  [MANUAL_TASK.category.charting]: 'charting',
  [MANUAL_TASK.category.coding]: 'coding',
  [MANUAL_TASK.category.billing]: 'billing',
  // Inbound-fax tasks are created by the fax subscription with no location, so only an "All locations"
  // Inbound Fax row can match them — see `UNLOCATED_TASK_CATEGORIES`, which is what keeps any other kind
  // of Inbound Fax row from existing.
  [FAX_TASK.category]: 'inboundFax',
  [MANUAL_TASK.category.other]: 'other',
};

export const getUiTaskCategoryForCode = (code: string | undefined): UiTaskCategoryId | undefined =>
  code ? TASK_CODE_TO_UI_CATEGORY[code] : undefined;

/**
 * Categories where EVERY task is created without a location tag, so a location filter on their settings row
 * could only ever match nothing — silently muting the category rather than narrowing it.
 *
 * Their rows are pinned to "all locations" by `normalizeNotificationPreferencesV2` (which repairs an already
 * stored selection) and the settings table renders their Locations cell read-only, so the dead state is
 * unreachable from either end.
 *
 * Only inbound fax qualifies: faxes arrive at the project's fax number, which maps to no Location. Note that
 * a category is NOT a candidate just because one of its producers omits the location — `erx` folds the
 * location-less DoseSpot subscription tasks and the always-located manual eRX tasks into one row, so pinning
 * it would throw away a filter that does work for half its tasks.
 */
export const UNLOCATED_TASK_CATEGORIES: readonly UiTaskCategoryId[] = ['inboundFax'];

/** Whether a category's tasks never carry a location, making its row's location filter meaningless. */
export const taskCategoryHasNoLocation = (id: UiTaskCategoryId): boolean => UNLOCATED_TASK_CATEGORIES.includes(id);

export interface ProviderNotificationPreferencesV2 {
  version: 2;
  virtualVisitScheduled: NotificationRowPref;
  waitingRoom: NotificationRowPref;
  taskCategories: Record<UiTaskCategoryId, NotificationRowPref>;
}

// The V2 extension URL, tag systems, and category coding system live in `practitioner.types.ts` alongside the
// sibling `provider-notifications-*` family (re-exported from the same barrel).

export const defaultNotificationRowPref = (
  enabled: boolean,
  method: ProviderNotificationMethod
): NotificationRowPref => ({
  enabled,
  method,
  locationIds: [],
  allLocations: true,
  assignedTo: 'anyone',
});

export const buildDefaultTaskCategoryPrefs = (
  enabled: boolean,
  method: ProviderNotificationMethod
): Record<UiTaskCategoryId, NotificationRowPref> => {
  const rows = {} as Record<UiTaskCategoryId, NotificationRowPref>;
  for (const id of UI_TASK_CATEGORY_IDS) {
    rows[id] = defaultNotificationRowPref(enabled, method);
  }
  return rows;
};

/**
 * Defensively fill in any missing rows/fields so callers can rely on a complete object even if the stored
 * JSON blob was written by an older version or is partial.
 */
export const normalizeNotificationPreferencesV2 = (
  prefs: Partial<ProviderNotificationPreferencesV2> | undefined,
  fallbackMethod: ProviderNotificationMethod = ProviderNotificationMethod['phone and computer']
): ProviderNotificationPreferencesV2 => {
  const normalizeRow = (row: Partial<NotificationRowPref> | undefined): NotificationRowPref => {
    const locationIds = Array.isArray(row?.locationIds) ? row!.locationIds : [];
    return {
      enabled: row?.enabled ?? false,
      method: row?.method ?? fallbackMethod,
      locationIds,
      // An empty location list always reads as "all locations" — an enabled row must never match nothing.
      allLocations: locationIds.length === 0 ? true : row?.allLocations ?? false,
      assignedTo: row?.assignedTo === 'me' ? 'me' : 'anyone',
    };
  };
  const taskCategories = {} as Record<UiTaskCategoryId, NotificationRowPref>;
  for (const id of UI_TASK_CATEGORY_IDS) {
    const row = normalizeRow(prefs?.taskCategories?.[id]);
    // A stored location selection on a category whose tasks carry no location matched nothing at all, which
    // read as "notifications for this category are broken". Repaired here, at the read boundary both the
    // settings page and the notifications cron go through, so anyone already muted this way is fixed on
    // deploy rather than on their next visit to the settings page.
    taskCategories[id] = taskCategoryHasNoLocation(id) ? { ...row, allLocations: true, locationIds: [] } : row;
  }
  return {
    version: 2,
    virtualVisitScheduled: normalizeRow(prefs?.virtualVisitScheduled),
    waitingRoom: normalizeRow(prefs?.waitingRoom),
    taskCategories,
  };
};

/** All notification rows in a V2 preferences object: the two telemed rows plus every task-category row. */
export const getAllNotificationRows = (prefs: ProviderNotificationPreferencesV2): NotificationRowPref[] => [
  prefs.virtualVisitScheduled,
  prefs.waitingRoom,
  ...UI_TASK_CATEGORY_IDS.map((id) => prefs.taskCategories[id]),
];

/**
 * Whether a notification row's location filter matches a given task/appointment location id.
 * A row with `allLocations` matches everything; a row with an explicit list matches only listed ids; a task
 * with no location (undefined) only matches `allLocations` rows.
 */
export const notificationRowMatchesLocation = (row: NotificationRowPref, locationId: string | undefined): boolean => {
  if (row.allLocations) return true;
  return locationId != null && row.locationIds.includes(locationId);
};

/* -------------------------------------------------------------------------------------------------
 * Zambda API contract for the notification bell and its settings page.
 *
 * The bell reads and writes exclusively through these endpoints, so no notification `Communication`
 * (or the `Encounter` behind its link) is ever handed to the browser. Each endpoint derives the
 * practitioner it acts for from the caller's token, which is also what authorizes it: there is no
 * recipient or practitioner id in any request shape below, so a caller cannot name someone else's
 * notifications or profile.
 * ------------------------------------------------------------------------------------------------- */

/**
 * Where a notification navigates. Names the destination rather than spelling a URL: routes belong to
 * the app that owns them, so a path change stays a frontend edit instead of a backend deploy.
 */
export type ProviderNotificationTarget =
  | { type: 'visit'; appointmentId: string }
  | { type: 'inboundFax'; faxCommunicationId: string };

/** One notification as the bell needs it — the whole of what the read endpoint discloses. */
export interface ProviderNotificationDto {
  id: string;
  message: string;
  isUnread: boolean;
  /** ISO instant the notification was sent; the bell renders it relative to now. */
  sentAt?: string;
  /** Absent when the notification has nowhere to go — the bell then renders it unclickable. */
  target?: ProviderNotificationTarget;
}

export interface GetProviderNotificationsOutput {
  /** Newest first, so the bell can render in order without sorting. */
  notifications: ProviderNotificationDto[];
}

export interface MarkProviderNotificationsReadInput {
  notificationIds: string[];
}

export interface MarkProviderNotificationsReadOutput {
  /**
   * The subset actually marked read: ids the caller doesn't own, and ids already read, are dropped
   * rather than reported as errors — an id that isn't yours must not be distinguishable from one
   * that doesn't exist.
   */
  markedReadIds: string[];
}

export interface UpdateProviderNotificationSettingsInput {
  preferences: ProviderNotificationPreferencesV2;
  /** Any format `isPhoneNumberValid` accepts; stored normalized. Omitted/invalid leaves the stored number alone. */
  phoneNumber?: string;
}

/** The normalized values actually stored, so the settings form can reseed from server truth. */
export type UpdateProviderNotificationSettingsOutput = UpdateProviderNotificationSettingsInput;
